/**
 * Formatting utilities for financial data
 */

/**
 * Format a number as USD currency
 */
export const formatCurrency = (
	value: number,
	options: {
		minimumFractionDigits?: number
		maximumFractionDigits?: number
		showCents?: boolean
	} = {},
): string => {
	const { minimumFractionDigits = 0, maximumFractionDigits = 0, showCents = false } = options

	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: showCents ? 2 : minimumFractionDigits,
		maximumFractionDigits: showCents ? 2 : maximumFractionDigits,
	}).format(value)
}

/**
 * Parse currency string to number
 */
export const parseCurrency = (value: string): number => {
	// Remove currency symbols, commas, and whitespace
	const cleaned = value.replace(/[$,\s]/g, '')
	const parsed = parseFloat(cleaned)
	return isNaN(parsed) ? 0 : parsed
}

/**
 * Format percentage
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
	return `${value.toFixed(decimals)}%`
}

/**
 * Generate quarter labels for a sequence of quarters
 * Properly handles year rollover (Q4 2024 → Q1 2025, not Q5 2024)
 */
export const generateQuarterLabels = (count: number, startYear?: number): string[] => {
	const year = startYear || new Date().getFullYear()
	return Array.from({ length: count }, (_, i) => {
		const quarterNum = (i % 4) + 1 // Q1, Q2, Q3, Q4, Q1...
		const yearOffset = Math.floor(i / 4) // 0 for first 4 quarters, 1 for next 4, etc.
		return `Q${quarterNum} ${year + yearOffset}`
	})
}

/**
 * Format quarter label from date
 */
export const formatQuarterLabel = (date: Date, fiscalYearStart: number = 1): string => {
	const month = date.getMonth() + 1
	const year = date.getFullYear()

	// Calculate quarter based on fiscal year start
	const adjustedMonth = ((month - fiscalYearStart + 12) % 12) + 1
	const quarter = Math.ceil(adjustedMonth / 3)

	// Determine fiscal year
	const fiscalYear = month >= fiscalYearStart ? year : year - 1

	return `Q${quarter} ${fiscalYear}`
}

/**
 * Format date for display
 */
export const formatDate = (date: Date): string => {
	return new Intl.DateTimeFormat('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	}).format(date)
}

/**
 * Format time ago (e.g., "2 minutes ago")
 */
export const formatTimeAgo = (date: Date): string => {
	const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)

	if (seconds < 60) return `${seconds}s ago`
	if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
	if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
	if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
	return formatDate(date)
}

/**
 * Format large numbers with abbreviations (K, M, B)
 */
export const formatCompactNumber = (value: number): string => {
	if (Math.abs(value) >= 1e9) {
		return `$${(value / 1e9).toFixed(1)}B`
	}
	if (Math.abs(value) >= 1e6) {
		return `$${(value / 1e6).toFixed(1)}M`
	}
	if (Math.abs(value) >= 1e3) {
		return `$${(value / 1e3).toFixed(1)}K`
	}
	return formatCurrency(value)
}

/**
 * Validate numeric input
 */
export const isValidNumber = (value: string): boolean => {
	// Allow numbers, negative sign, and decimal point
	return /^-?\d*\.?\d*$/.test(value) || value === '' || value === '-'
}

/**
 * Format file size
 */
export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 Bytes'

	const k = 1024
	const sizes = ['Bytes', 'KB', 'MB', 'GB']
	const i = Math.floor(Math.log(bytes) / Math.log(k))

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}
